// Plan-BundlesImplementation §E.2 — usage bar with credit segments.
//
// Replaces the inline `UsageBar` for resources where bundle credits can
// extend the monthly base allowance. Renders a stacked-segment bar:
//
//   ┌─────────────────────────────────────────────────────────────┐
//   │██████████│▓▓▓▓│▒▒▒▒│░░░░│   (unused — remaining cap)        │
//   └─────────────────────────────────────────────────────────────┘
//     base         credit  credit  credit
//     used         #1      #2      #3        + overage segment if any
//
// FIFO color order (oldest-expiring darkest, matches consume_resource()
// read order on the backend). Hover/tap reveals the §E.2 tooltip with
// exact counts + expiry dates.
//
// Overage segment: pure derivation — anything consumed past `base +
// totalCredits`. Visible only when the workspace has auto_overage
// enabled for this resource (we don't surface "you might overage" as
// a phantom segment).

import React, { useMemo, useState } from 'react';
import type { ActiveCredit, BundleResource } from '../../types/billing';

interface Props {
  label: string;
  /** Total units consumed this billing cycle. */
  used: number;
  /** Monthly base allowance from the plan. -1 = unlimited (renders as a
   *  flat 10% bar matching the legacy UsageBar). */
  baseLimit: number;
  /** Active credit rows for THIS resource only, in FIFO order
   *  (oldest-expiring first). UsageBarWithCredits does NOT filter —
   *  caller pre-filters. */
  credits: ActiveCredit[];
  /** Unit label for the right-side counter ("min", "convos", "scrapes"…). */
  unit: string;
  /** True when `workspaces.auto_overage[resource]` is on — controls
   *  whether the overage tail segment is rendered when used exceeds
   *  base + credits. */
  autoOverageEnabled: boolean;
  /** Optional — used by the tooltip to label the resource for the
   *  /credits row "oldest expires DATE" line. Defaults to lowercased
   *  label. */
  resourceKey?: BundleResource;
}

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const formatUnit = (n: number): string => n.toLocaleString();

// Per-credit-segment color shade. Index 0 = oldest expiring = darkest.
// We cap at 4 distinct shades; additional credits collapse onto the
// lightest shade. Emerald palette throughout for credit segments to
// differentiate from base (primary/blue) and overage (amber).
const CREDIT_SHADES = [
  'bg-emerald-700',
  'bg-emerald-600',
  'bg-emerald-500',
  'bg-emerald-400',
];

const CREDIT_SHADE_DARK = [
  'dark:bg-emerald-500',
  'dark:bg-emerald-400',
  'dark:bg-emerald-300',
  'dark:bg-emerald-200',
];

const UsageBarWithCredits: React.FC<Props> = ({
  label,
  used,
  baseLimit,
  credits,
  unit,
  autoOverageEnabled,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const unlimited = baseLimit < 0;

  const {
    baseUsed,
    creditsRemaining,
    creditTotalInitial,
    overageUsed,
    totalCap,
    segments,
  } = useMemo(() => {
    // Resolve "where did consumption land".
    const baseCap = unlimited ? Number.POSITIVE_INFINITY : Math.max(0, baseLimit);
    let remaining = used;
    let baseUsedLocal = Math.min(remaining, baseCap);
    remaining -= baseUsedLocal;

    let creditTotalInitialLocal = 0;
    let creditsRemainingLocal = 0;
    // Build per-credit "consumed" + "remaining" pairs in FIFO order.
    const creditSegments: Array<{
      credit: ActiveCredit;
      consumed: number; // consumed portion of THIS credit row this cycle
      remaining: number; // remaining portion (display)
    }> = [];
    for (const c of credits) {
      creditTotalInitialLocal += c.amount_initial;
      creditsRemainingLocal += c.amount;
      // For the bar we approximate this cycle's pull as the remaining
      // consumption flowing into this credit row. `amount_initial -
      // amount` is "lifetime consumption since purchase" — close enough
      // for the segment-fill visual; precise per-cycle attribution
      // would need a backend field we don't have today.
      const fillFromThisCycle = Math.min(remaining, c.amount_initial);
      remaining -= fillFromThisCycle;
      creditSegments.push({
        credit: c,
        consumed: fillFromThisCycle,
        remaining: c.amount, // exact remaining from backend
      });
    }

    const overageUsedLocal = autoOverageEnabled ? Math.max(0, remaining) : 0;
    const totalCapLocal = unlimited
      ? Math.max(used, 1)
      : baseCap + creditTotalInitialLocal + (autoOverageEnabled ? overageUsedLocal : 0);

    return {
      baseUsed: baseUsedLocal,
      creditsRemaining: creditsRemainingLocal,
      creditTotalInitial: creditTotalInitialLocal,
      overageUsed: overageUsedLocal,
      totalCap: totalCapLocal,
      segments: creditSegments,
    };
  }, [used, baseLimit, credits, autoOverageEnabled, unlimited]);

  const pct = (n: number): number => {
    if (totalCap <= 0) return 0;
    return Math.min(100, (n / totalCap) * 100);
  };

  // Base color follows the legacy UsageBar heat map (emerald → amber → red)
  // but only on the BASE segment — credits stay emerald-shaded, overage stays
  // amber. This keeps "how much of base remains" visually obvious.
  const basePct = unlimited ? 0 : (baseUsed / Math.max(1, baseLimit)) * 100;
  const baseColor = unlimited
    ? 'bg-emerald-500'
    : basePct >= 100
    ? 'bg-red-500'
    : basePct >= 80
    ? 'bg-amber-500'
    : 'bg-primary';

  const unitSuffix = unit === 'min' ? ' min' : '';

  // Right-side counter — mirror the legacy UsageBar copy when no
  // credits, expand to "X / base+credits cap" when credits exist.
  const counterLine = useMemo(() => {
    if (unlimited) {
      return `${formatUnit(used)}${unitSuffix} / ∞`;
    }
    if (creditTotalInitial > 0) {
      const totalAvail = baseLimit + creditTotalInitial;
      return `${formatUnit(used)} / ${formatUnit(totalAvail)}${unitSuffix}`;
    }
    return `${formatUnit(used)} / ${formatUnit(baseLimit)}${unitSuffix}`;
  }, [unlimited, used, baseLimit, creditTotalInitial, unitSuffix]);

  // Oldest expiry (FIFO first non-exhausted credit row).
  const oldestExpiringCredit = useMemo(
    () => credits.find((c) => c.amount > 0) ?? null,
    [credits],
  );

  return (
    <div
      className="space-y-1.5 relative"
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
      onClick={() => setTooltipOpen((x) => !x)}
    >
      <div className="flex items-center justify-between text-xs sm:text-sm">
        <span className="font-medium text-gray-700 dark:text-gray-300 truncate">{label}</span>
        <span className="text-gray-500 dark:text-gray-400 font-mono text-[11px] sm:text-xs flex-shrink-0">
          {counterLine}
        </span>
      </div>

      {/* Multi-segment bar */}
      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex">
        {/* Base used */}
        {baseUsed > 0 && (
          <div
            className={`h-full ${baseColor} transition-all`}
            style={{
              width: unlimited
                ? '10%'
                : `${Math.max(pct(baseUsed), baseUsed > 0 ? 2 : 0)}%`,
            }}
            title={`Base: ${formatUnit(baseUsed)}${unitSuffix} used`}
          />
        )}

        {/* Per-credit used segments (FIFO order, darkest first) */}
        {segments.map((seg, i) => {
          if (seg.consumed <= 0) return null;
          const shadeIdx = Math.min(i, CREDIT_SHADES.length - 1);
          return (
            <div
              key={`${seg.credit.id}-used`}
              className={`h-full ${CREDIT_SHADES[shadeIdx]} ${CREDIT_SHADE_DARK[shadeIdx]} transition-all`}
              style={{ width: `${pct(seg.consumed)}%` }}
              title={`Credits: ${formatUnit(seg.consumed)}${unitSuffix} from bundle expiring ${formatDate(seg.credit.expires_at)}`}
            />
          );
        })}

        {/* Overage segment — only when auto-overage is enabled AND we
            spilled past base+credits. */}
        {overageUsed > 0 && autoOverageEnabled && (
          <div
            className="h-full bg-amber-500 transition-all"
            style={{ width: `${pct(overageUsed)}%` }}
            title={`Overage: ${formatUnit(overageUsed)}${unitSuffix} this cycle`}
          />
        )}
      </div>

      {/* Tooltip line below the bar — always visible when there's
          interesting state to convey. Mirrors the §E.2 spec copy. */}
      {(creditTotalInitial > 0 || overageUsed > 0 || tooltipOpen) && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
          {!unlimited && (
            <>
              <span className="text-gray-600 dark:text-gray-400">
                Base: {formatUnit(baseUsed)} / {formatUnit(baseLimit)} used
              </span>
              {creditTotalInitial > 0 && (
                <>
                  {' · '}
                  <span className="text-emerald-600 dark:text-emerald-400">
                    Credits: {formatUnit(creditsRemaining)} remaining
                    {oldestExpiringCredit && (
                      <> (oldest expires {formatDate(oldestExpiringCredit.expires_at)})</>
                    )}
                  </span>
                </>
              )}
              {overageUsed > 0 && (
                <>
                  {' · '}
                  <span className="text-amber-600 dark:text-amber-400">
                    Overage: {formatUnit(overageUsed)} this cycle
                  </span>
                </>
              )}
              {/* When credits exist but no overage, hint at the safety net */}
              {creditTotalInitial > 0 && overageUsed === 0 && !autoOverageEnabled && baseUsed >= baseLimit && (
                <>
                  {' · '}
                  <span className="text-gray-500 dark:text-gray-500 italic">
                    Auto-overage off
                  </span>
                </>
              )}
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default UsageBarWithCredits;
